import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function AdminDashboard({ user }) {
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [activities, setActivities] = useState([]);
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (user.role !== 'admin') {
      alert('Acceso denegado: Solo administradores');
      navigate('/');
      return;
    }
    loadStats();
    loadPendingRequests();
  }, [user, navigate]);

  const loadStats = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/dashboard/stats?admin_email=${encodeURIComponent(user.email)}`
      );
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadPendingRequests = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/teachers/pending?admin_email=${encodeURIComponent(user.email)}`
      );
      if (response.ok) {
        const data = await response.json();
        setPendingRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Error loading pending requests:', error);
    }
  };

  const loadTeachers = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/teachers/list?admin_email=${encodeURIComponent(user.email)}`
      );
      if (response.ok) {
        const data = await response.json();
        setTeachers(data.teachers || []);
      }
    } catch (error) {
      console.error('Error loading teachers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActivities = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/activities/all?admin_email=${encodeURIComponent(user.email)}`
      );
      if (response.ok) {
        const data = await response.json();
        setActivities(data.activities || []);
      }
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (email) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/teachers/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_email: user.email,
          teacher_email: email
        })
      });

      if (response.ok) {
        alert('Profesor aprobado correctamente');
        loadPendingRequests();
        loadStats();
      } else {
        const data = await response.json();
        alert('Error: ' + data.detail);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleRemoveTeacher = async (email) => {
    if (!window.confirm(`¿Eliminar aprobación de ${email}?`)) return;

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/teachers/${encodeURIComponent(email)}?admin_email=${encodeURIComponent(user.email)}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        alert('Profesor eliminado correctamente');
        loadTeachers();
        loadStats();
      } else {
        const data = await response.json();
        alert('Error: ' + data.detail);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleDeleteActivity = async (type, id) => {
    if (!window.confirm('¿Eliminar esta actividad?')) return;

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/activities/${type}/${id}?admin_email=${encodeURIComponent(user.email)}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        alert('Actividad eliminada correctamente');
        loadActivities();
        loadStats();
      } else {
        const data = await response.json();
        alert('Error: ' + data.detail);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Selecciona un archivo JSON');
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);
    formData.append('admin_email', user.email);

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/teachers/import`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        setImportFile(null);
        loadStats();
        if (activeTab === 'teachers') loadTeachers();
      } else {
        const data = await response.json();
        alert('Error: ' + data.detail);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/teachers/export?admin_email=${encodeURIComponent(user.email)}`
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'teachers.json';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Error al exportar');
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };


  const loadContainers = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/containers/persistent?admin_email=${encodeURIComponent(user.email)}`
      );
      if (response.ok) {
        const data = await response.json();
        setContainers(data.containers || []);
      }
    } catch (error) {
      console.error('Error loading containers:', error);
      alert('Error al cargar contenedores');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContainer = async (containerId, containerName) => {
    if (!window.confirm(`¿Eliminar el contenedor "${containerName}"?\n\nEsta acción no se puede deshacer.`)) {
      return;
    }

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/containers/${containerId}?admin_email=${encodeURIComponent(user.email)}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        alert('Contenedor eliminado correctamente');
        loadContainers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Error al eliminar contenedor');
      }
    } catch (error) {
      console.error('Error deleting container:', error);
      alert('Error al eliminar contenedor');
    }
  };

  const handleDeleteAllContainers = async () => {
    if (!window.confirm(`⚠️ ¿Eliminar TODOS los contenedores (${containers.length})?\n\nEsta acción eliminará:\n- Contenedores activos\n- Contenedores persistentes\n- Todo el progreso de los estudiantes\n\n¿Estás seguro?`)) {
      return;
    }

    if (!window.confirm('Esta es tu última oportunidad. ¿Realmente quieres eliminar TODOS los contenedores?')) {
      return;
    }

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/containers/delete-all?admin_email=${encodeURIComponent(user.email)}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        const data = await response.json();
        alert(`✅ ${data.deleted_count} contenedores eliminados correctamente`);
        loadContainers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Error al eliminar contenedores');
      }
    } catch (error) {
      console.error('Error deleting all containers:', error);
      alert('Error al eliminar contenedores');
    }
  };


  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'teachers') loadTeachers();
    if (tab === 'activities') loadActivities();
    if (tab === 'containers') loadContainers();
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>⚙️ Panel de Administración</h1>
        <button style={styles.logoutButton} onClick={() => navigate('/teacher')}>
          ← Volver al Dashboard
        </button>
      </div>

      <div style={styles.tabs}>
        <button
          style={activeTab === 'stats' ? styles.activeTab : styles.tab}
          onClick={() => handleTabChange('stats')}
        >
          📊 Estadísticas
        </button>
        <button
          style={activeTab === 'pending' ? styles.activeTab : styles.tab}
          onClick={() => handleTabChange('pending')}
        >
          ⏳ Solicitudes ({pendingRequests.length})
        </button>
        <button
          style={activeTab === 'teachers' ? styles.activeTab : styles.tab}
          onClick={() => handleTabChange('teachers')}
        >
          👨‍🏫 Profesores
        </button>
        <button
          style={activeTab === 'activities' ? styles.activeTab : styles.tab}
          onClick={() => handleTabChange('activities')}
        >
          📚 Actividades
        </button>
        <button
          style={activeTab === 'containers' ? styles.activeTab : styles.tab}
          onClick={() => handleTabChange('containers')}
        >
          🐳 Contenedores Docker
        </button>
      </div>

      <div style={styles.content}>
        {activeTab === 'stats' && stats && (
          <div>
            <h2>📊 Resumen General</h2>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>{stats.users.total}</div>
                <div style={styles.statLabel}>Total Usuarios</div>
                <div style={styles.statDetails}>
                  {stats.users.students} estudiantes<br />
                  {stats.users.teachers} profesores<br />
                  {stats.users.admins} admins
                </div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>{stats.activities.total}</div>
                <div style={styles.statLabel}>Total Actividades</div>
                <div style={styles.statDetails}>
                  {stats.activities.quizzes} quizzes<br />
                  {stats.activities.hangman} ahorcado<br />
                  {stats.activities.linux_quests} terminal quests<br />
                  {stats.activities.texthunt || 0} texthunt
                </div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>{stats.pending_requests}</div>
                <div style={styles.statLabel}>Solicitudes Pendientes</div>
              </div>
            </div>

            {stats.recent_teachers && stats.recent_teachers.length > 0 && (
              <div style={styles.section}>
                <h3>👨‍🏫 Profesores Recientes</h3>
                <div style={styles.list}>
                  {stats.recent_teachers.map((teacher, idx) => (
                    <div key={idx} style={styles.listItem}>
                      <strong>{teacher.name}</strong> ({teacher.email})<br />
                      <small>Aprobado: {new Date(teacher.approved_at).toLocaleDateString()}</small>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'pending' && (
          <div>
            <h2>⏳ Solicitudes Pendientes</h2>
            {pendingRequests.length === 0 ? (
              <p style={styles.emptyMessage}>No hay solicitudes pendientes</p>
            ) : (
              <div style={styles.list}>
                {pendingRequests.map((request) => (
                  <div key={request.id} style={styles.requestCard}>
                    <div>
                      <strong>{request.name}</strong><br />
                      {request.email}<br />
                      <small>Solicitado: {new Date(request.requested_at).toLocaleDateString()}</small>
                    </div>
                    <button
                      style={styles.approveButton}
                      onClick={() => handleApprove(request.email)}
                    >
                      ✅ Aprobar
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'teachers' && (
          <div>
            <div style={styles.sectionHeader}>
              <h2>👨‍🏫 Profesores Aprobados</h2>
              <div style={styles.actions}>
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  style={styles.fileInput}
                />
                {importFile && (
                  <button style={styles.button} onClick={handleImport}>
                    📥 Importar
                  </button>
                )}
                <button style={styles.button} onClick={handleExport}>
                  📤 Exportar JSON
                </button>
              </div>
            </div>

            {loading ? (
              <p>Cargando...</p>
            ) : teachers.length === 0 ? (
              <p style={styles.emptyMessage}>No hay profesores aprobados</p>
            ) : (
              <div style={styles.table}>
                <table style={styles.tableElement}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Nombre</th>
                      <th style={styles.th}>Email</th>
                      <th style={styles.th}>Actividades</th>
                      <th style={styles.th}>Aprobado</th>
                      <th style={styles.th}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teachers.map((teacher, idx) => (
                      <tr key={idx}>
                        <td style={styles.td}>{teacher.name}</td>
                        <td style={styles.td}>{teacher.email}</td>
                        <td style={styles.td}>
                          {teacher.activities.total}
                          <br />
                          <small>
                            Q:{teacher.activities.quizzes} 
                            H:{teacher.activities.hangman} 
                            T:{teacher.activities.linux_quests}
                            TX:{teacher.activities.texthunt || 0}
                          </small>
                        </td>
                        <td style={styles.td}>
                          {teacher.approved_at
                            ? new Date(teacher.approved_at).toLocaleDateString()
                            : 'N/A'}
                        </td>
                        <td style={styles.td}>
                          <button
                            style={styles.deleteButton}
                            onClick={() => handleRemoveTeacher(teacher.email)}
                          >
                            🗑️ Eliminar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'activities' && (
          <div>
            <h2>📚 Todas las Actividades</h2>
            {loading ? (
              <p>Cargando...</p>
            ) : activities.length === 0 ? (
              <p style={styles.emptyMessage}>No hay actividades</p>
            ) : (
              <div style={styles.table}>
                <table style={styles.tableElement}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Tipo</th>
                      <th style={styles.th}>Título</th>
                      <th style={styles.th}>Profesor</th>
                      <th style={styles.th}>Creado</th>
                      <th style={styles.th}>Detalles</th>
                      <th style={styles.th}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map((activity, idx) => (
                      <tr key={idx}>
                        <td style={styles.td}>
                          {activity.type === 'quiz' ? '📝 Quiz' : 
                           activity.type === 'hangman' ? '🎯 Ahorcado' :
                           activity.type === 'linux_quest' ? '💻 Terminal Quest' :
                           activity.type === 'text_hunt' ? '🔍 TextHunt' : activity.type}
                        </td>
                        <td style={styles.td}>{activity.title}</td>
                        <td style={styles.td}>{activity.teacher_name}</td>
                        <td style={styles.td}>
                          {activity.created_at
                            ? new Date(activity.created_at).toLocaleDateString()
                            : 'N/A'}
                        </td>
                        <td style={styles.td}>
                          {activity.questions_count && `${activity.questions_count} preguntas`}
                          {activity.words_count && `${activity.words_count} palabras`}
                          {activity.levels_count && `${activity.levels_count} niveles`}
                          {activity.questions_count && `${activity.questions_count} preguntas`}
                        </td>
                        <td style={styles.td}>
                          <button
                            style={styles.deleteButton}
                            onClick={() => handleDeleteActivity(activity.type, activity.id)}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'containers' && (
          <div>
            <div style={styles.sectionHeader}>
              <h2>🐳 Contenedores Docker Persistentes</h2>
              <div style={styles.actions}>
                <button 
                  style={styles.button} 
                  onClick={loadContainers}
                  disabled={loading}
                >
                  🔄 Refrescar
                </button>
                {containers.length > 0 && (
                  <button
                    style={{...styles.deleteButton, padding: '10px 20px'}}
                    onClick={handleDeleteAllContainers}
                  >
                    🗑️ Eliminar Todos ({containers.length})
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <p>Cargando contenedores...</p>
            ) : containers.length === 0 ? (
              <div style={styles.emptyMessage}>
                <p>No hay contenedores Docker activos</p>
                <small style={{color: '#666'}}>
                  Los contenedores persistentes aparecerán aquí cuando los estudiantes inicien Terminal Quests
                </small>
              </div>
            ) : (
              <div style={styles.table}>
                <table style={styles.tableElement}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Nombre</th>
                      <th style={styles.th}>Estado</th>
                      <th style={styles.th}>Quest</th>
                      <th style={styles.th}>Estudiante</th>
                      <th style={styles.th}>Creado</th>
                      <th style={styles.th}>Puertos</th>
                      <th style={styles.th}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {containers.map((container, idx) => (
                      <tr key={idx}>
                        <td style={styles.td}>
                          <strong>{container.name}</strong>
                          <br />
                          <small style={{color: '#999', fontSize: '11px'}}>
                            {container.container_id.substring(0, 12)}
                          </small>
                        </td>
                        <td style={styles.td}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            backgroundColor: container.status === 'running' ? '#d4edda' : '#f8d7da',
                            color: container.status === 'running' ? '#155724' : '#721c24'
                          }}>
                            {container.status}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <strong>{container.quest_name}</strong>
                          <br />
                          <small style={{color: '#999'}}>ID: {container.quest_id.substring(0, 8)}</small>
                        </td>
                        <td style={styles.td}>
                          <strong>{container.student_name || 'Unknown Student'}</strong>
                          <br />
                          <small style={{color: '#999'}}>ID: {container.student_id.substring(0, 8)}</small>
                        </td>
                        <td style={styles.td}>
                          <small>
                            {container.created 
                              ? new Date(container.created).toLocaleString('es-ES', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : 'N/A'}
                          </small>
                        </td>
                        <td style={styles.td}>
                          <small style={{fontFamily: 'monospace'}}>
                            {container.ports && Object.keys(container.ports).length > 0
                              ? Object.keys(container.ports).join(', ')
                              : 'N/A'}
                          </small>
                        </td>
                        <td style={styles.td}>
                          <button
                            style={styles.deleteButton}
                            onClick={() => handleDeleteContainer(container.container_id, container.name)}
                          >
                            🗑️ Eliminar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    padding: '20px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '10px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
  },
  title: {
    margin: 0,
    color: '#333'
  },
  logoutButton: {
    padding: '10px 20px',
    backgroundColor: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  tabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px'
  },
  tab: {
    padding: '15px 25px',
    backgroundColor: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease'
  },
  activeTab: {
    padding: '15px 25px',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
    boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
  },
  content: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '10px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginTop: '20px'
  },
  statCard: {
    backgroundColor: '#f8f9fa',
    padding: '25px',
    borderRadius: '10px',
    textAlign: 'center',
    border: '2px solid #e9ecef'
  },
  statNumber: {
    fontSize: '48px',
    fontWeight: 'bold',
    color: '#667eea',
    marginBottom: '10px'
  },
  statLabel: {
    fontSize: '18px',
    color: '#333',
    marginBottom: '10px'
  },
  statDetails: {
    fontSize: '14px',
    color: '#666'
  },
  section: {
    marginTop: '30px'
  },
  list: {
    marginTop: '20px'
  },
  listItem: {
    padding: '15px',
    backgroundColor: '#f8f9fa',
    marginBottom: '10px',
    borderRadius: '8px',
    border: '1px solid #e9ecef'
  },
  requestCard: {
    padding: '20px',
    backgroundColor: '#f8f9fa',
    marginBottom: '15px',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  approveButton: {
    padding: '10px 20px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  actions: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center'
  },
  button: {
    padding: '10px 20px',
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  deleteButton: {
    padding: '8px 15px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  fileInput: {
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '5px'
  },
  emptyMessage: {
    textAlign: 'center',
    color: '#999',
    fontSize: '16px',
    padding: '40px'
  },
  table: {
    overflowX: 'auto',
    marginTop: '20px'
  },
  tableElement: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    padding: '12px',
    backgroundColor: '#f8f9fa',
    borderBottom: '2px solid #dee2e6',
    textAlign: 'left',
    fontWeight: 'bold'
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #dee2e6'
  }
};

export default AdminDashboard;
